/**
 * 공유 시트 인쇄 (BOOTH-59) — 부스 `printMode === "share"` 일 때 `window.print()` 대신 결과 JPEG 를
 * Web Share API 로 iOS 공유 시트에 넘기고 운영자가 "프린트" 를 고른다.
 *
 * 왜: iPad AirPrint 는 Safari 문서 인쇄(`window.print`)에 여백 있는 미디어("엽서")만 내놓아 SELPHY 같은
 * 소형 사진 프린터에서 사방 ~3mm 흰 테두리가 남고, 프린터 본체의 "테두리 없음" 설정도 적용되지 않는다.
 * 사진 앱처럼 이미지 파일을 인쇄로 보내면 사진용(테두리 없음) 기본값을 잡을 가능성이 있어 실험 경로로 둔다.
 *
 * 순수 함수만 — `navigator` 를 인자로 받아 vitest 로 판정을 검사한다.
 */

/** `navigator.share({ files })` 를 쓸 수 있는가. canShare 가 없거나 파일 공유를 거부하면 false → dialog 폴백. */
export function canShareFile(nav: Pick<Navigator, "share" | "canShare"> | undefined, file: File): boolean {
  if (!nav || typeof nav.share !== "function" || typeof nav.canShare !== "function") return false;
  try {
    return nav.canShare({ files: [file] });
  } catch {
    return false;
  }
}

/** 공유 시트·프린트 대화상자에 보이는 파일명. shortCode 가 없으면(업로드 전) 타임스탬프. */
export function shareFileName(shortCode: string | null, mimeType: string): string {
  const ext = mimeType === "image/png" ? "png" : "jpg";
  const stem = shortCode?.trim() ? shortCode.trim() : String(Date.now());
  return `fiket-${stem}.${ext}`;
}

/**
 * `navigator.share` 결과 분류. 취소(AbortError)는 손님이 시트를 닫은 것이라 화면에 남고,
 * 그 외 실패(NotAllowedError = 사용자 제스처 밖, TypeError = 파일 미지원 등)는 dialog 폴백.
 */
export type ShareOutcome = "shared" | "cancelled" | "unsupported";

export function classifyShareError(err: unknown): Exclude<ShareOutcome, "shared"> {
  const name = (err as { name?: string } | null)?.name;
  return name === "AbortError" ? "cancelled" : "unsupported";
}

/** dataURL → File. 타입은 dataURL 헤더에서 읽고(없으면 jpeg), 파일명은 `shareFileName`. */
export async function dataUrlToFile(dataUrl: string, shortCode: string | null): Promise<File> {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  const type = blob.type || "image/jpeg";
  return new File([blob], shareFileName(shortCode, type), { type });
}
